import type { Restaurant } from '@shared/types/restaurant';
import { roundMoney } from '@shared/utils/money';
import type { BookingContextValue } from '../state/types';
import { activeSlotMenuFor } from './activeSlotMenu';
import { calculatePairingsFee, type FeeBreakdown } from './calculateFee';

/**
 * The USD a card issued outside Argentina is charged for this deposit, built the
 * way the backend builds `feeUSDDeposit`: the USD guarantee (kids at their USD
 * amount when set, else at the adult amount), USD pairings, then tip, then tax.
 * Null when the note does not apply or there is no USD amount to state.
 */
export function foreignCardUsdTotal(
  booking: BookingContextValue,
  restaurant: Restaurant,
  breakdown: FeeBreakdown,
): number | null {
  if ((restaurant.currency ?? '').toUpperCase() !== 'ARS' || restaurant.settleUsd) return null;
  if (breakdown.guaranteeType !== 'deposit' || !(breakdown.total > 0)) return null;
  // A checkout surcharge is charged on the USD amount on one production path and not on the other: state nothing.
  if (breakdown.serviceFee > 0) return null;

  const slot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;
  const menu = activeSlotMenuFor(booking, restaurant.publicSearchMode);

  let unitUsd = 0;
  let kidsUnitUsd = 0;
  if (breakdown.guaranteeSource === 'menu') {
    unitUsd = menu?.guarantee?.feeUSD ?? 0;
    kidsUnitUsd = menu?.guarantee?.feeKidsUSD ?? 0;
  } else if (breakdown.guaranteeSource === 'slot') {
    // `slot.feeUSD` is already multiplied by the party; `allFees.depositUSD` is the per-person amount.
    unitUsd = slot?.allFees?.depositUSD ?? 0;
    // The slot payload carries no USD kids amount, so the USD a party with kids pays is unknown here.
    if (booking.kids && booking.howManyKids > 0) return null;
  }
  if (!(unitUsd > 0)) return null;

  const party = booking.diners;
  const kids = booking.kids ? Math.min(booking.howManyKids, party) : 0;
  let base: number;
  if (!breakdown.perPerson) {
    base = unitUsd;
  } else if (kids > 0 && kidsUnitUsd > 0) {
    base = unitUsd * (party - kids) + kidsUnitUsd * kids;
  } else {
    base = unitUsd * party;
  }

  const subtotal = base + calculatePairingsFee(booking.selectedPairings, true);
  const tipPercent = breakdown.gratuity > 0 ? breakdown.gratuityPercent : 0;
  const taxPercent = breakdown.salesTax > 0 ? breakdown.salesTaxPercent : 0;
  const withTip = subtotal + subtotal * tipPercent / 100;
  const withTax = withTip + withTip * taxPercent / 100;
  return roundMoney(withTax);
}
